import qs from 'qs';

export default class ApiService {
  constructor() {
    this.prefix = '/api/v1';
  }

  loadAllPages(url, key, data = []) {
    const vm = this;
    const getPage = function (url) {
      return axios.get(url)
        .then((response) => {
          data.push.apply(data, response.data[key]);
          const nextPageUrl = vm.nextPageUrl(response.headers);
          if (nextPageUrl) {
            return getPage(nextPageUrl);
          }
          return data;
        })
        .catch((error) => {
          throw error;
        });
    };
    return getPage(url);
  }

  nextPageUrl(headers) {
    const vm = this;
    if (!headers || !headers.link) {
      return null;
    }

    const paginationLinks = headers.link.split(',');
    let nextPageUrl = null;

    paginationLinks.forEach((l) => {
      const nextRel = 'rel="next"';
      if (l.indexOf(nextRel) !== -1) {
        const regExp = /\<([^>]+)>/;
        nextPageUrl = regExp.exec(l)[1];
      }
    });

    return nextPageUrl;
  }

  // Urls
  accountsUrl() {
    return `${this.prefix}/accounts`;
  }

  // Metrics
  // measure: 'net' (default, net revenue) or 'count' (transactions)
  categoryBreakdownNet(range, intervalGroup, customStartDate, customEndDate, locationId, measure = 'net') {
    let url = `${this.prefix}/metrics/category_breakdown_net?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    if (measure && measure !== 'net') {
      url += `&measure=${measure}`;
    }
    return url;
  }

  membershipsByPlan(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/memberships_by_plan?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  categoryBreakdownCount(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/category_breakdown_count?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  coachPrivateLessonEventsUrl(coachId, startAt = null, endAt = null) {
    const expand = ['event_bookings', 'private_lessons'];
    let url = `${this.prefix}/coaches/${coachId}/events?available_for_booking=true&event_type=private_lesson&${qs.stringify({ expand }, { arrayFormat: 'brackets' })}`;
    if (startAt) {
      url += `&start_at=${encodeURIComponent(startAt)}`;
    }
    if (endAt) {
      url += `&end_at=${encodeURIComponent(endAt)}`;
    }
    return url;
  }

  bookPrivateLessonUrl(eventId) {
    return `${this.prefix}/events/${eventId}/book`;
  }

  checkoutPrivateLessonUrl(lessonId) {
    return `${this.prefix}/private_lessons/${lessonId}/checkout`;
  }

  eventBookingUrl(eventBookingId) {
    return `${this.prefix}/event_bookings/${eventBookingId}`;
  }

  grossVolume(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/gross_volume?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  mrr(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/mrr?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  // Deprecated
  newSubscribers(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/new_subscribers?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  newSubscriptions(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/new_subscriptions?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  canceledSubscriptions(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/cancelled_subscriptions?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  subscriptionFlow(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/subscription_flow?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  renewedSubscriptions(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/renewed_subscriptions?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  revPerSubscriber(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/revenue_per_subscriber?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  chargeCount(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/charge_count?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  chargeAvg(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/charge_avg?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  activeSubscribers(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/active_subscribers?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  netVolume(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/net_volume?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  churnRate(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/churn_rate?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  trialsPurchased(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/trials_purchased?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  trialsCompleted(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/trials_completed?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  trialConversionRate(range, intervalGroup, customStartDate, customEndDate, locationId) {
    let url = `${this.prefix}/metrics/trial_conversion_rate?interval_group=${intervalGroup}&range=${range}`;
    if (customStartDate && customEndDate) {
      url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
    }
    if (locationId) {
      url += `&location_id=${locationId}`;
    }
    return url;
  }

  accountSwitchUrl() {
    return `${this.prefix}/accounts/switch`;
  }

  billingProfileUrl(billingProfileId) {
    return `${this.prefix}/billing_profiles/${billingProfileId}`;
  }

  teamBillingProfileUrl(forUpdate) {
    return `${this.prefix}/billing_profiles/team${forUpdate ? '?for_update=true' : ''}`;
  }

  addOnSubscribeUrl() {
    return `${this.prefix}/add_ons`;
  }

  coachUrl(coachId) {
    return `${this.prefix}/coaches/${coachId}`;
  }

  coachesUrl() {
    return `${this.prefix}/coaches`;
  }

  coachesAcceptingPrivateLessonsUrl() {
    return `${this.prefix}/coaches?accepting_private_lessons=true`;
  }

  couponsUrl() {
    return `${this.prefix}/coupons`;
  }

  couponUrl(couponId) {
    return `${this.prefix}/coupons/${couponId}`;
  }

  checkInsUrl(eventId) {
    return `${this.prefix}/events/${eventId}/check_ins`;
  }

  customersUrl() {
    return `${this.prefix}/customers`;
  }

  eventTeamScoresUrl(eventId) {
    return `${this.prefix}/events/${eventId}/team_scores`;
  }

  eventCheckInUrl(eventId, checkInId) {
    return `${this.prefix}/events/${eventId}/check_ins/${checkInId}`;
  }

  eventMatchInvitesUrl(eventId) {
    return `${this.prefix}/events/${eventId}/match_invites`;
  }

  eventWrestlersUrl(eventId) {
    return `${this.prefix}/events/${eventId}/wrestlers`;
  }

  documentsUrl() {
    return `${this.prefix}/documents`;
  }

  updateDocumentUrl(docId) {
    return `${this.prefix}/documents/${docId}`;
  }

  documentUrl(docId) {
    return `${this.prefix}/documents/${docId}`;
  }

  signAgainDocumentUrl(docId) {
    return `${this.prefix}/documents/${docId}/sign_again`;
  }

  teamScoreUrl(teamScoreId) {
    return `${this.prefix}/team_scores/${teamScoreId}`;
  }

  eventTeamScoreUrl(eventId, teamScoreId) {
    return `${this.prefix}/events/${eventId}/team_scores/${teamScoreId}`;
  }

  wrestlerPendingAgreementsUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/agreements?status=pending`;
  }

  parentPendingAgreementsUrl(parentId) {
    return `${this.prefix}/parents/${parentId}/agreements?status=pending`;
  }

  coachPendingAgreementsUrl(coachId) {
    return `${this.prefix}/coaches/${coachId}/agreements?status=pending`;
  }

  paymentPlanPreviewUrl() {
    return `${this.prefix}/payment_plans/preview`;
  }

  paymentPlansUrl() {
    return `${this.prefix}/payment_plans`;
  }

  checkoutPreviewUrl() {
    return '/api/v2/checkouts/preview';
  }

  checkoutsUrl() {
    return '/api/v2/checkouts';
  }

  paymentPlanUrl(planId) {
    return `${this.prefix}/payment_plans/${planId}`;
  }

  approvePaymentPlanUrl(paidSessionId, planId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/payment_plans/${planId}/approve`;
  }

  denyPaymentPlanUrl(paidSessionId, planId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/payment_plans/${planId}/deny`;
  }

  removeWrestlerPaymentPlanUrl(planId, wrestlerId) {
    return `${this.prefix}/payment_plans/${planId}/wrestler_profiles/${wrestlerId}`;
  }

  membershipsUrl() {
    return `${this.prefix}/memberships`;
  }

  mergeJobsUrl() {
    return `${this.prefix}/merge_jobs`;
  }

  previewMembershipUrl() {
    return `${this.prefix}/memberships/preview`;
  }

  printJobsUrl() {
    return `${this.prefix}/print_jobs`;
  }

  manualInvoiceUrl(invoiceId) {
    return `${this.prefix}/manual_invoices/${invoiceId}`;
  }

  manualInvoicesUrl() {
    return `${this.prefix}/manual_invoices`;
  }

  messagesUrl(groupId) {
    return `${this.prefix}/message_groups/${groupId}/messages`;
  }

  messageVersionsUrl(groupId, messageId) {
    return `${this.prefix}/message_groups/${groupId}/messages/${messageId}/versions`;
  }

  messageSearchUrl() {
    return `${this.prefix}/search/messages`;
  }

  // v2 returns the leaner conversation-list payload (member_count,
  // other_avatar_url); v1 keeps its fuller shape for older native apps.
  messageGroupsV2Url() {
    return '/api/v2/message_groups';
  }

  liveDraftUrl(groupId) {
    return `${this.prefix}/message_groups/${groupId}/messages/live_draft`;
  }

  messageMembershipsUrl(groupId) {
    return `${this.prefix}/message_groups/${groupId}/memberships`;
  }

  messageMembershipUrl(membershipId) {
    return `${this.prefix}/message_memberships/${membershipId}`;
  }

  messageReactionsUrl(messageId) {
    return `${this.prefix}/messages/${messageId}/reactions`;
  }

  messageReactionUrl(messageId, reactionId) {
    return `${this.prefix}/messages/${messageId}/reactions/${reactionId}`;
  }

  pollVotesUrl(pollId) {
    return `${this.prefix}/polls/${pollId}/poll_votes`;
  }

  pollVoteUrl(pollId, voteId) {
    return `${this.prefix}/polls/${pollId}/poll_votes/${voteId}`;
  }

  pollCloseUrl(pollId) {
    return `${this.prefix}/polls/${pollId}/close`;
  }

  pollReopenUrl(pollId) {
    return `${this.prefix}/polls/${pollId}/reopen`;
  }

  pollNonVotersUrl(pollId) {
    return `${this.prefix}/polls/${pollId}/non_voters`;
  }

  pollVotersUrl(pollId) {
    return `${this.prefix}/polls/${pollId}/voters`;
  }

  pollOptionVotesUrl(optionId) {
    return `${this.prefix}/poll_options/${optionId}/poll_votes`;
  }

  notificationPreferencesUrl() {
    return `${this.prefix}/notification_preferences`;
  }

  notificationPreferenceUrl(prefId) {
    return `${this.prefix}/notification_preferences/${prefId}`;
  }

  gearStoreRequestsUrl() {
    return `${this.prefix}/gear_store_requests`;
  }

  onlineStoresUrl() {
    return `${this.prefix}/online_stores`;
  }

  onlineStoreUrl(storeId) {
    return `${this.prefix}/online_stores/${storeId}`;
  }

  userUrl(userId) {
    return `${this.prefix}/users/${userId}`;
  }

  readMessagesUrl(groupId) {
    return `${this.prefix}/message_groups/${groupId}/read`;
  }

  messageGroupsUrl() {
    return `${this.prefix}/message_groups`;
  }

  messageGroupUrl(groupId) {
    return `${this.prefix}/message_groups/${groupId}`;
  }

  messageGroupSyncUrl(groupId) {
    return `${this.prefix}/message_groups/${groupId}/sync`;
  }

  guardianWrestlersUrl(guardianType, guardianId) {
    return `${this.prefix}/guardians/${guardianType}/${guardianId}/wrestlers`;
  }

  wrestlerGuardiansUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/guardians`;
  }

  guardianRelationshipUrl() {
    return `${this.prefix}/guardian_relationships`;
  }

  kioskTokensUrl() {
    return `${this.prefix}/kiosk_tokens`;
  }

  kioskTokenUrl(id) {
    return `${this.prefix}/kiosk_tokens/${id}`;
  }

  personalAccessTokensUrl() {
    return `${this.prefix}/personal_access_tokens`;
  }

  personalAccessTokenUrl(id) {
    return `${this.prefix}/personal_access_tokens/${id}`;
  }

  parentsUrl() {
    return `${this.prefix}/parents`;
  }

  parentUrl(pId) {
    return `${this.prefix}/parents/${pId}`;
  }

  parentReactivateUrl(pId) {
    return `${this.prefix}/parents/${pId}/reactivate`;
  }

  profileEventSubscriptionsUrl() {
    return `${this.prefix}/profile_event_subscriptions`;
  }

  profileEventSubscriptionUrl(id) {
    return `${this.prefix}/profile_event_subscriptions/${id}`;
  }

  productsUrl() {
    return `${this.prefix}/products`;
  }

  productUrl(productId) {
    return `${this.prefix}/products/${productId}`;
  }

  registrationQuestions(profileType, sessionParam) {
    let url = null;

    if (profileType === 'ParentProfile') {
      url = `${this.prefix}/registration_questions?for_type=parents`;
    } else if (profileType === 'WrestlerProfile') {
      url = `${this.prefix}/registration_questions?for_type=wrestlers`;
    } else if (profileType === 'CoachProfile') {
      url = `${this.prefix}/registration_questions?for_type=coaches`;
    } else if (profileType === 'CheckIn') {
      url = `${this.prefix}/registration_questions?for_type=check_ins`;
    } else if (profileType === 'Prospect') {
      url = `${this.prefix}/registration_questions?for_type=prospects`;
    }

    if (url && sessionParam) {
      url = `${url}&paid_session=${sessionParam}`;
    }

    return url;
  }

  upsellsUrl() {
    return `${this.prefix}/upsells`;
  }

  upsellUrl(upsellId) {
    return `${this.prefix}/upsells/${upsellId}`;
  }

  wrestlerPropertiesForReports() {
    return `${this.prefix}/registration_questions?for_type=wrestlers&paid_session=properties&for_reports=true`;
  }

  privateWrestlerPropertiesForReports() {
    return `${this.prefix}/registration_questions?for_type=wrestlers&visibility=private&for_reports=true`;
  }

  privateWrestlerProperties() {
    return `${this.prefix}/registration_questions?for_type=wrestlers&visibility=private`;
  }

  privateWrestlerPropertyAnswers(wrestlerId) {
    return `${this.prefix}/registration_answers?profile_id=${wrestlerId}&profile_type=wrestlerprofile&visibility=private`;
  }

  registrationAnswers(profileId, profileType, checkInId, sessionParam) {
    let url = `${this.prefix}/registration_answers?profile_id=${profileId}&profile_type=${profileType}`;
    if (checkInId) {
      url = `${url}&check_in_id=${checkInId}`;
    }
    if (sessionParam) {
      url = `${url}&paid_session_id=${sessionParam}`;
    }

    return url;
  }

  signatureUrl() {
    return `${this.prefix}/signatures`;
  }

  searchUrl() {
    return `${this.prefix}/search`;
  }

  guardianExactSearchUrl(firstName, lastName) {
    return `${this.prefix}/search?search_type=guardian_exact_match&first_name=${firstName}&last_name=${lastName}`;
  }

  attachmentsUrl() {
    return `${this.prefix}/attachments`;
  }

  s3SignatureUrl() {
    return `${this.prefix}/attachments/s3_signature`;
  }

  s3ConfirmUrl() {
    return `${this.prefix}/attachments/s3_confirm`;
  }

  eventNotifyUrl(eventId) {
    return `${this.prefix}/events/${eventId}/notify`;
  }

  eventsUrl(params) {
    return `${this.prefix}/events${params}`;
  }

  eventUrl(eventId) {
    return `${this.prefix}/events/${eventId}`;
  }

  recentEventTemplatesUrl() {
    return `${this.prefix}/events/recent_templates`;
  }

  eventTemplateUrl(eventId) {
    return `${this.prefix}/events/${eventId}/template`;
  }

  eventUrlWithBookings(eventId) {
    const expand = ['event_bookings', 'private_lessons'];
    return `${this.prefix}/events/${eventId}?${qs.stringify({ expand }, { arrayFormat: 'brackets' })}`;
  }

  deleteEventRecurringUrl(eventId) {
    return `${this.prefix}/events/${eventId}?delete_recurring=true`;
  }

  deleteEventFutureRecurringUrl(eventId) {
    return `${this.prefix}/events/${eventId}?delete_recurring=future`;
  }

  // Admin command center aggregates. Window params (window_start/window_end,
  // breakdown_start/breakdown_end) ride along as axios params at the call site.
  privateLessonAdminStatsUrl() {
    return `${this.prefix}/private_lessons/admin_stats`;
  }

  privateLessonRevenueStatsUrl() {
    return `${this.prefix}/private_lessons/revenue_stats`;
  }

  privateLessonPayrollSummaryUrl() {
    return `${this.prefix}/private_lessons/payroll_summary`;
  }

  privateLessonRemindCollectionUrl() {
    return `${this.prefix}/private_lessons/remind_collection`;
  }

  privateLessonPayrollLessonsUrl(coachId, startAt, endAt) {
    const params = { coach_profile_id: coachId, start_at: startAt, end_at: endAt };
    return `${this.prefix}/private_lessons/payroll_lessons?${qs.stringify(params)}`;
  }

  coachPayoutsUrl(coachId = null) {
    if (coachId) {
      return `${this.prefix}/coach_payouts?${qs.stringify({ coach_id: coachId })}`;
    }
    return `${this.prefix}/coach_payouts`;
  }

  coachPayoutUrl(payoutId) {
    return `${this.prefix}/coach_payouts/${payoutId}`;
  }

  // Bulk-closes a coach's open private lesson slots starting on/after a
  // YYYY-MM-DD date. preview=true returns the would-be count without deleting.
  deletePrivateLessonSlotsUrl(coachId, after, preview = false) {
    const params = { coach_id: coachId, after };
    if (preview) {
      params.preview = true;
    }
    return `${this.prefix}/events/private_lesson_slots?${qs.stringify(params)}`;
  }

  eventInvitesUrl(eventId) {
    return `${this.prefix}/events/${eventId}/event_invites`;
  }

  eventInviteBatchUrl(eventId) {
    return `${this.prefix}/events/${eventId}/event_invites/batch`;
  }

  eventInviteUrl(eventInviteId) {
    return `${this.prefix}/event_invites/${eventInviteId}`;
  }

  invitesUrl() {
    return `${this.prefix}/invites`;
  }

  tagsUrl() {
    return `${this.prefix}/tags`;
  }

  automationsUrl() {
    return `${this.prefix}/automations`;
  }

  automationUrl(automationId) {
    return `${this.prefix}/automations/${automationId}`;
  }

  tagUrl(tagId) {
    return `${this.prefix}/tags/${tagId}`;
  }

  inviteUrl(token) {
    return `${this.prefix}/invites/${token}`;
  }

  resendInviteUrl(token) {
    return `${this.prefix}/invites/${token}/resend`;
  }

  matchUrl(matchId) {
    return `${this.prefix}/matches/${matchId}`;
  }

  matchesUrl() {
    return `${this.prefix}/matches`;
  }

  matchScoreEvents(matchId) {
    return `${this.prefix}/matches/${matchId}/scoring_events`;
  }

  rulesetUrl(name) {
    return `${this.prefix}/scoring_rules/${name}`;
  }

  pairingCriteriaUrl() {
    return `${this.prefix}/pairing_criteria`;
  }

  eventMatchesUrl(eventId) {
    return `${this.prefix}/events/${eventId}/matches`;
  }

  funnelsUrl() {
    return `${this.prefix}/funnels`;
  }

  funnelRemoveWrestlerUrl(wrestlerId) {
    return `${this.prefix}/funnels/wrestlers/${wrestlerId}`;
  }

  funnelEditStepUrl() {
    return `${this.prefix}/funnels/edit_step`;
  }

  fundraiserUrl(fundraiserId) {
    return `${this.prefix}/fundraisers/${fundraiserId}`;
  }

  // Get the billing profile for a given profile. Booking a lesson passes the slot's event id, which lets a
  // brand-new account with nothing else to pay get a card form (JustifiTokenThrottle#justifi_booking_open_lesson?)
  profileBillingProfileUrl(profileId, profileType, eventId = null) {
    return `${this.prefix}/billing_profiles/${profileType}/${profileId}?for_update=true${eventId ? `&event_id=${eventId}` : ''}`;
  }

  eventBookingsUrl(eventId) {
    if (eventId) {
      return `${this.prefix}/events/${eventId}/bookings`;
    }
    return `${this.prefix}/event_bookings`;
  }

  fundraisersUrl() {
    return `${this.prefix}/fundraisers`;
  }

  fundraiserPricesUrl(fundraiserId) {
    return `${this.prefix}/fundraisers/${fundraiserId}/billing_plans`;
  }

  billingPlans(params) {
    return `${this.prefix}/billing_plans?${qs.stringify(params, { arrayFormat: 'brackets' })}`;
  }

  matchFeedbacksUrl(matchId) {
    return `${this.prefix}/matches/${matchId}/feedbacks`;
  }

  matchNotesUrl(matchId) {
    return `${this.prefix}/matches/${matchId}/notes`;
  }

  updateNoteUrl(noteId) {
    return `${this.prefix}/notes/${noteId}`;
  }

  opposingTeamsUrl() {
    return `${this.prefix}/opposing_teams`;
  }

  opposingTeamUrl(opposingTeamId) {
    return `${this.prefix}/opposing_teams/${opposingTeamId}`;
  }

  opposingWrestlersUrl() {
    return `${this.prefix}/opposing_wrestlers`;
  }

  originalVideoUrl(originalVideoId) {
    return `${this.prefix}/original_videos/${originalVideoId}`;
  }

  originalVideosUrl() {
    return `${this.prefix}/original_videos`;
  }

  futureSessionsUrl() {
    return `${this.prefix}/paid_sessions?type=ends_in_future`;
  }

  recurringSessionsUrl() {
    return `${this.prefix}/paid_sessions?type=recurring`;
  }

  // todo clean this up
  registerableSessionsUrl(forGuests, recurringSessions) {
    let filter = 'registerable';
    if (forGuests) {
      filter = 'guest_registerable';
    }
    if (recurringSessions) {
      filter = 'recurring';
    }
    return `${this.prefix}/paid_sessions?type=${filter}`;
  }

  paidSessionsUrl() {
    return `${this.prefix}/paid_sessions`;
  }

  paidSessionUrl(sessionId) {
    return `${this.prefix}/paid_sessions/${sessionId}`;
  }

  paymentOptionsUrl() {
    return `${this.prefix}/payment_options`;
  }

  paymentOptionUrl(optionId) {
    return `${this.prefix}/payment_options/${optionId}`;
  }

  paidSessionBillingPlansUrl(paidSessionId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/billing_plans`;
  }

  divisionStandardsUrl() {
    return `${this.prefix}/division_standards`;
  }

  divisionStandardUrl(id) {
    return `${this.prefix}/division_standards/${id}`;
  }

  locationsUrl() {
    return `${this.prefix}/locations`;
  }

  locationUrl(id) {
    return `${this.prefix}/locations/${id}`;
  }

  paidSessionDocumentsUrl(paidSessionId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/documents`;
  }

  billingPlanUrl(planId) {
    return `${this.prefix}/billing_plans/${planId}`;
  }

  practicePlansUrl() {
    return `${this.prefix}/practice_plans`;
  }

  practicePlanUrl(planId) {
    return `${this.prefix}/practice_plans/${planId}`;
  }

  practicePlanBlockCategoriesUrl() {
    return `${this.prefix}/practice_plan_block_categories`;
  }

  practicePlanBlocksUrl(planId) {
    return `${this.prefix}/practice_plans/${planId}/blocks`;
  }

  practicePlanBlockUrl(planId, blockId) {
    return `${this.prefix}/practice_plans/${planId}/blocks/${blockId}`;
  }

  practicePlanNotifyUrl(planId) {
    return `${this.prefix}/practice_plans/${planId}/notify`;
  }

  refundUrl() {
    return `${this.prefix}/refunds`;
  }

  registrationAnswersUrl() {
    return `${this.prefix}/registration_answers/batch`;
  }

  reportsUrl() {
    return `${this.prefix}/reports`;
  }

  reportUrl(reportId) {
    return `${this.prefix}/reports/${reportId}`;
  }

  teamUrl(teamId) {
    return `${this.prefix}/teams/${teamId}`;
  }

  templateBlocksUrl() {
    return `${this.prefix}/practice_plan_blocks`;
  }

  templateBlockUrl(templateId) {
    return `${this.prefix}/practice_plan_blocks/${templateId}`;
  }

  opponentScoutingAnswersUrl(opponentId) {
    return `${this.prefix}/opposing_wrestlers/${opponentId}/scouting_answers`;
  }

  opponentScoutingAnswerUrl(opponentId, scoutingAnswerId) {
    return `${this.prefix}/opposing_wrestlers/${opponentId}/scouting_answers/${scoutingAnswerId}`;
  }

  scoutingAnswerUrl(scoutingAnswerId) {
    return `${this.prefix}/scouting_answers/${scoutingAnswerId}`;
  }

  scoutingQuestionsUrl() {
    return `${this.prefix}/scouting_questions`;
  }

  athleteIdUrl() {
    return `${this.prefix}/athlete_identifiers`;
  }

  usawMemberships(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/usaw_memberships`;
  }

  usawMembership(wrestlerId, usawId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/usaw_memberships/${usawId}`;
  }

  usawVerifications(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/usaw_verifications`;
  }

  aauMemberships(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/aau_memberships`;
  }

  aauMembership(wrestlerId, aauId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/aau_memberships/${aauId}`;
  }

  wrestlerUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}`;
  }

  expandedRostersWrestlerUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}?expand_rosters=true`;
  }

  expandedAnswersWrestlerUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}?expand_registration_answers=true`;
  }

  wrestlerRosterMembershipsUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/roster_memberships`;
  }

  wrestlersUrl() {
    return `${this.prefix}/wrestlers`;
  }

  wrestlerReactivateUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/reactivate`;
  }

  wrestlerCheckIns(wrestlerId, params) {
    return `${this.prefix}/wrestlers/${wrestlerId}/check_ins?${qs.stringify(params, { arrayFormat: 'brackets' })}`;
  }

  wrestlerCheckInsSummaryUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/check_ins/summary`;
  }

  wrestlerWeightEntriesUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/weight_entries`;
  }

  wrestlerWeightEntryUrl(wrestlerId, weightEntryId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/weight_entries/${weightEntryId}`;
  }

  filteredWrestlers(includeGuests, rosterIds, sortBy, perPage, rosterFilterStrategy, expandSubscriptions = false) {
    const params = [];
    params.push('expand_rosters=true');

    if (expandSubscriptions) {
      params.push('expand_subscriptions=true');
    }

    if (includeGuests) {
      params.push('include_guests=true');
    }

    if (sortBy) {
      params.push(`order=${sortBy}`);
    }

    if (rosterFilterStrategy) {
      params.push(`roster_filter_strategy=${rosterFilterStrategy}`);
    }

    if (rosterIds.length > 0) {
      rosterIds.forEach((rId) => {
        params.push(`roster_ids[]=${rId}`);
      });
    }

    params.push(`per_page=${perPage}`);

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return `${this.prefix}/wrestlers${queryString}`;
  }

  // Team-wide name search (ignores roster filters server-side via ransack).
  // Used by the attendance roll call to surface wrestlers outside the current
  // roster filter. A multi-word term is treated as "first last".
  wrestlerNameSearch(query) {
    const trimmed = query.trim();
    const parts = trimmed.split(/\s+/);
    const q = {
      profile_type_in: ['teammate', 'guest'],
      s: 'last_name asc',
    };

    if (parts.length > 1) {
      q.first_name_cont = parts[0];
      q.last_name_cont = parts.slice(1).join(' ');
    } else {
      q.first_name_or_last_name_cont = trimmed;
    }

    const params = {
      expand_rosters: true,
      per_page: 10,
      q,
    };
    return `${this.prefix}/wrestlers?${qs.stringify(params, { arrayFormat: 'brackets' })}`;
  }

  wrestlerNotesUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/notes`;
  }

  rostersUrl() {
    return `${this.prefix}/rosters`;
  }

  rosterUrl(rosterId) {
    return `${this.prefix}/rosters/${rosterId}`;
  }

  ransackRostersUrl(ransackParams) {
    return `${this.prefix}/rosters?${this.formatRansackParams(ransackParams)}`;
  }

  formatRansackParams(params) {
    return qs.stringify(params, { arrayFormat: 'brackets' });
  }

  rosterUrl(rosterId) {
    return `${this.prefix}/rosters/${rosterId}`;
  }

  rosterSyncUrl(rosterId) {
    return `${this.prefix}/rosters/${rosterId}/sync`;
  }

  rosterMembershipsUrl(rosterId) {
    return `${this.prefix}/rosters/${rosterId}/roster_memberships`;
  }

  rosterWrestlersUrl(rosterId) {
    return `${this.prefix}/rosters/${rosterId}/wrestler_profiles`;
  }

  registrationQuestionUrl(questionId) {
    return `${this.prefix}/registration_questions/${questionId}`;
  }

  syncGuestRegQuestionsUrl() {
    return `${this.prefix}/registration_questions/guest/sync_all`;
  }

  registrationQuestionsUrl() {
    return `${this.prefix}/registration_questions`;
  }

  subscriptionUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}`;
  }

  renewSubscriptionUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/renew`;
  }

  subscriptionDeleteCouponUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/coupon`;
  }

  subscriptionCouponUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/coupon`;
  }

  subscriptionChangePlanUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/plan/replace`;
  }

  subscriptionChangeExpiringPlanUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/plan/change_expiring`;
  }

  subscriptionPausePaymentsUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/pause`;
  }

  subscriptionResumePaymentsUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/resume`;
  }

  subscriptionPauseOptionsUrl(subscriptionId) {
    return `${this.prefix}/billing_subscriptions/${subscriptionId}/pause_options`;
  }

  paidSessionPauseSubscriptionsUrl(paidSessionId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/pause_subscriptions`;
  }

  paidSessionResumeSubscriptionsUrl(paidSessionId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/resume_subscriptions`;
  }

  paidSessionCancelScheduledPausesUrl(paidSessionId) {
    return `${this.prefix}/paid_sessions/${paidSessionId}/cancel_scheduled_pauses`;
  }

  wrestlerBillingSubscriptions(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/billing_subscriptions`;
  }

  wrestlerRegistrationsUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/registrations`;
  }

  wrestlerChargesUrl(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/charges`;
  }

  // guardianPath: "parents/12" or "coaches/7" (a parent's or coach's Payments tab)
  guardianBillingSubscriptionsUrl(guardianPath) {
    return `${this.prefix}/${guardianPath}/billing_subscriptions`;
  }

  guardianRegistrationsUrl(guardianPath) {
    return `${this.prefix}/${guardianPath}/registrations`;
  }

  paymentFollowUpsUrl() {
    return `${this.prefix}/payment_follow_ups`;
  }

  paymentFollowUpsSummaryUrl() {
    return `${this.prefix}/payment_follow_ups/summary`;
  }

  paymentFollowUpUrl(id) {
    return `${this.prefix}/payment_follow_ups/${id}`;
  }

  guardianChargesUrl(guardianPath) {
    return `${this.prefix}/${guardianPath}/charges`;
  }

  wrestlerClassPass(wrestlerId, passId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/class_passes/${passId}`;
  }

  wrestlerClassPasses(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/class_passes`;
  }

  availableWrestlerClassPasses(wrestlerId) {
    return `${this.prefix}/wrestlers/${wrestlerId}/class_passes?status=available`;
  }

  guardianBillingSubscriptions(guardianType, guardianId) {
    if (guardianType === 'CoachProfile') {
      return `${this.prefix}/coaches/${guardianId}/billing_subscriptions`;
    }

    return `${this.prefix}/parents/${guardianId}/billing_subscriptions`;
  }
}
